// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { apiClient } from '../services/api-client.js';
import type { LogoutResponse } from '@vn-grc/shared';
import { userRecordIdOf } from '../utils/user-record-id.js';
import { logWarning } from '../utils/optional-operation-log.js';
import { forgetUnlinkedUpdateInstances } from '../utils/unlinked-update-instances.js';

/** Proactive refresh interval: refresh session every 25 minutes (well before 30-min expiry). */
const REFRESH_INTERVAL_MS = 25 * 60 * 1000;

interface UserInfo {
  /**
   * The session identity: `grc_users._id`, the value every record's actor field
   * holds. Derived through `userRecordIdOf` (rehearsal R1) — never the UUID
   * `user_id` field a setup- or Users-created account also stores. Read it
   * through `userRecordIdOf(user)` as well, so the rule has one home.
   */
  user_id: string;
  email: string;
  name: string;
  role_name: string;
  role_id: string;
  org_id: string;
  permissions: string[];
  mfa_required?: boolean;
  /**
   * Sprint 63.7.0 — the organisation requires a second factor for this account
   * and it has none, so this session reaches only the enrolment paths.
   *
   * Distinct from `mfa_required`, which means *you have a factor and have not
   * presented it yet*. The remedy for one is a code; the remedy for the other
   * is enrolling, and `ProtectedRoute` sends them to different places.
   */
  mfa_enrolment_required?: boolean;
  /**
   * Sprint 63.29.0 (#541) — an administrator requires this person to choose a
   * new password; the session reaches only the change-password step until they
   * do. `ProtectedRoute` sends them there.
   */
  password_change_required?: boolean;
  /**
   * Sprint 63.29.0 (#579) — permissions held through a live delegation, beside
   * the role's own `permissions`. `PermissionContext` offers their union for
   * what the person may do, and the role's list alone for what they may grant.
   */
  delegated_permissions?: string[];
}

interface AuthState {
  isAuthenticated: boolean;
  loading: boolean;
  user: UserInfo | null;
}

interface AuthContextValue extends AuthState {
  login: (user: any) => void;
  logout: () => void;
  /**
   * Sprint 63.6.3, gate E12.5 pass 2 — re-read the session from the server
   * (`GET /auth/me`, which a session held at the TOTP step-up is always admitted
   * to) and replace the UI state with it. The step-up page calls it after a code
   * is accepted so the shell is built from what the server now says, with no
   * `mfa_required` left over from the sign-in that carried it. Resolves `true`
   * when a session was read, `false` when there is none (the 401 handler has
   * then already cleared the state).
   */
  refreshAuth: () => Promise<boolean>;
  /**
   * Sprint 63.6.3 (AC12) — true after a logout of a session that arrived through
   * SSO whose identity-provider session could NOT be ended (a SAML session, or
   * an OIDC provider that declares no end-session endpoint). The login page
   * says so in words; it is cleared by the next sign-in. Never set for a
   * password session, and never set when the page is being sent to the
   * provider to end it there.
   */
  providerSessionNotEnded: boolean;
}

// Exported so consumers that need a non-throwing read of auth state
// (e.g. components rendered in tests without an AuthProvider) can use
// `useContext(AuthContext)` directly and get `null` instead of an
// exception.
//
// fix-sprint-20260411-0417 Finding 15 — FormSectionRenderer reads the
// active org id to key the semantic-options cache; the throwing
// `useAuth` is unsuitable for that case because the component is
// instantiated by tests that mock api-client without mounting an
// AuthProvider.
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth provider that manages session state via httpOnly cookies.
 * Tokens are managed by the backend (set/cleared via Set-Cookie headers).
 * Frontend only tracks authentication state and user info.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    loading: true,
    user: null,
  });

  const [providerSessionNotEnded, setProviderSessionNotEnded] = useState(false);

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /*
    Sprint 62.57.0, gate E12.5 pass 2 (FE2-2) — what one person kept in this
    tab for later is not handed to the next. A password sign-out does not reload
    the window, so the module state and the tab's `sessionStorage` outlive it.
    The kept draft update form (`utils/unlinked-update-instances.ts`) is
    forgotten on an explicit sign-out (`logout`) and whenever the session
    settles on a different person or organisation than the last one signed in
    on this tab: a sign-in as someone else, a re-read that answers with
    another account.

    Gate E12.5 pass 3 (FE3-4) — a session that merely ENDS (a 401: idle
    expiry, a laptop that slept) forgets nothing. The same person signing back
    in must find the instance they kept, or preparing the update again creates
    a second draft form and leaves the first among the assessment's forms (the
    orphan FE-3 exists to prevent). Keeping it hands nothing to anyone else:
    both stores are keyed `org:user:original` and an entry whose owner is not
    the reader is never read; if another person signs in next, the change of
    person forgets it anyway.
  */
  const settledIdentity = state.loading
    ? undefined
    : state.user
      ? `${state.user.org_id}:${state.user.user_id}`
      : null;
  // The last person signed in on this tab; not cleared by a session that ends.
  const lastPersonRef = useRef<string | null>(null);
  useEffect(() => {
    if (!settledIdentity) return;
    const before = lastPersonRef.current;
    lastPersonRef.current = settledIdentity;
    if (before !== null && before !== settledIdentity) forgetUnlinkedUpdateInstances();
  }, [settledIdentity]);

  // Proactive session refresh: call /auth/refresh periodically to keep the
  // session alive while the user is on the page. The backend sliding window
  // handles active API usage, but this covers idle-but-present scenarios
  // (e.g. reading a long report without clicking).
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(async () => {
      try {
        // The refresh cookie is httpOnly and sent automatically.
        // Passing empty object — backend reads token from cookie first.
        await apiClient.refreshToken({} as any);
        /*
         * Gate E12.5 pass 1 (frontend P1-2) — re-read the delegated grants at
         * every refresh: a delegation that started, ended or was revoked since
         * sign-in changes what this person may do. Only that field is replaced,
         * so the hold flags the sign-in carried are untouched.
         */
        const profile: any = await apiClient.getMe();
        const delegated = Array.isArray(profile?.delegated_permissions)
          ? (profile.delegated_permissions as string[])
          : [];
        setState((prev) =>
          prev.user ? { ...prev, user: { ...prev.user, delegated_permissions: delegated } } : prev,
        );
      } catch {
        // A failed reread cannot establish that delegated UI grants remain live.
        // Keep identity/base grants/hold flags; the shared 401 handler still owns logout.
        setState((prev) =>
          prev.user ? { ...prev, user: { ...prev.user, delegated_permissions: [] } } : prev,
        );
        logWarning('auth.delegated_grant_refresh_failed');
      }
    }, REFRESH_INTERVAL_MS);
  }, []);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Wire auto-logout on 401 responses (F-H5)
  useEffect(() => {
    apiClient.setOnUnauthorized(() => {
      stopRefreshTimer();
      setState({
        isAuthenticated: false,
        loading: false,
        user: null,
      });
    });
  }, [stopRefreshTimer]);

  // On mount, validate session by calling getMe() — cookie is sent automatically
  useEffect(() => {
    apiClient
      .getMe()
      .then((profile: any) => {
        setState({
          isAuthenticated: true,
          loading: false,
          user: userInfoFromProfile(profile),
        });
        startRefreshTimer();
      })
      .catch(() => {
        // No valid session cookie — user is not authenticated
        setState({
          isAuthenticated: false,
          loading: false,
          user: null,
        });
      });

    return () => stopRefreshTimer();
  }, [startRefreshTimer, stopRefreshTimer]);

  const refreshAuth = async (): Promise<boolean> => {
    try {
      const profile: any = await apiClient.getMe();
      setState({ isAuthenticated: true, loading: false, user: userInfoFromProfile(profile) });
      startRefreshTimer();
      return true;
    } catch {
      return false;
    }
  };

  const login = (user: any) => {
    // Cookie was set by the backend login response — just update UI state
    const userInfo: UserInfo = {
      user_id: userRecordIdOf(user) ?? '',
      email: user.email || '',
      name: user.name || '',
      role_name: user.role_name || '',
      role_id: user.role_id || '',
      org_id: user.org_id || '',
      permissions: user.permissions || [],
      mfa_required: user.mfa_required || false,
      mfa_enrolment_required: user.mfa_enrolment_required || false,
      password_change_required: user.password_change_required === true,
      delegated_permissions: Array.isArray(user.delegated_permissions)
        ? user.delegated_permissions
        : [],
    };
    setState({
      isAuthenticated: true,
      loading: false,
      user: userInfo,
    });
    setProviderSessionNotEnded(false);
    startRefreshTimer();
  };

  const logout = () => {
    stopRefreshTimer();
    // FE2-2 / FE3-4 — a person who signs out leaves nothing kept on this tab.
    forgetUnlinkedUpdateInstances();
    /*
     * Call backend logout, which clears the httpOnly cookies and ends the
     * platform session BEFORE it answers.
     *
     * Sprint 63.6.3 (AC11, AC12) — the answer says what happens to the
     * identity provider's session:
     *
     *  - `provider_logout_url` set: the provider declared an end-session
     *    endpoint, so the window goes there. The platform session is already
     *    ended, so a failed or abandoned redirect leaves the person signed out
     *    of ComplianceOne, never half-in. The server validates the URL when it
     *    is stored; this is defence in depth (gate E12.5 pass 1): the window
     *    is sent only to a value that parses as an `https:` URL. Anything else
     *    (`javascript:`, `data:`, a relative path, plain `http:`, garbage) is
     *    treated as no URL at all, so a federated session gets the "not ended"
     *    notice below rather than a navigation to an unchecked string.
     *  - `federated` with no URL: the session arrived through SSO and the
     *    provider's own session was NOT ended. The login page says so. The
     *    platform does not pretend a sign-out reached somewhere it did not.
     *  - otherwise (a password session): nothing is added.
     *
     * The UI state is cleared at once, as before, so the redirect never waits
     * on the provider and a slow answer cannot leave a signed-out person on an
     * authenticated screen.
     */
    apiClient
      .logoutApi()
      .then((response: LogoutResponse | undefined) => {
        const providerUrl = httpsUrlOrNull(response?.provider_logout_url);
        if (providerUrl) {
          window.location.assign(providerUrl);
        } else if (response?.federated) {
          setProviderSessionNotEnded(true);
        }
      })
      .catch((err) => console.warn('logoutApi failed', err));
    setState({
      isAuthenticated: false,
      loading: false,
      user: null,
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshAuth, providerSessionNotEnded }}>
      {children}
    </AuthContext.Provider>
  );
}

/** The UI's view of a session, from `GET /auth/me`. */
function userInfoFromProfile(profile: any): UserInfo {
  const user = profile.user;
  const role = profile.role;
  return {
    user_id: userRecordIdOf(user) ?? '',
    email: user.email,
    name: user.name,
    role_name: role.name || '',
    role_id: role.role_id || '',
    org_id: user.org_id || '',
    permissions: role.permissions || [],
    password_change_required: user.password_change_required === true,
    delegated_permissions: Array.isArray(profile.delegated_permissions)
      ? profile.delegated_permissions
      : [],
  };
}

/**
 * The value as an absolute `https:` URL string, or null. Used for the one place
 * the platform navigates the window to an address a server handed back.
 */
function httpsUrlOrNull(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  try {
    const parsed = new URL(value.trim());
    // The string handed back is navigated to as given; `href` would normalise it.
    return parsed.protocol === 'https:' ? value.trim() : null;
  } catch {
    return null;
  }
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
